import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { patchSettingsBlob } from '@/lib/settingsStore';

export type Theme = 'iron' | 'bloom' | 'pulse' | 'forest' | 'cobalt';
export type WeightUnit = 'lb' | 'kg';
export type DoseUnit = 'mg' | 'mcg' | 'ml';

interface Settings {
  theme: Theme;
  dark: boolean;
  weightUnit: WeightUnit;
  doseUnit: DoseUnit;
}

const DEFAULT: Settings = {
  theme: 'pulse',
  dark: false,
  weightUnit: 'lb',
  doseUnit: 'mg',
};

const STORAGE_KEY = 'optimize.settings.v1';

interface ThemeContextValue extends Settings {
  setTheme: (t: Theme) => void;
  setDark: (d: boolean) => void;
  setWeightUnit: (u: WeightUnit) => void;
  setDoseUnit: (u: DoseUnit) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// Blazor persisted these as PascalCase keys with integer enum values
// (e.g. {"Theme":2,"Dark":false,"WeightUnit":0,"DoseUnit":0}). Tolerate that
// shape so existing users don't lose their preferences on first React load.
// Order is append-only — Blazor's persisted enum used the original three
// indices (0=iron, 1=bloom, 2=pulse). New themes go on the end so legacy
// PascalCase + integer payloads continue to coerce correctly.
const THEMES_BY_INDEX: readonly Theme[] = ['iron', 'bloom', 'pulse', 'forest', 'cobalt'];
const WEIGHT_UNITS_BY_INDEX: readonly WeightUnit[] = ['lb', 'kg'];
const DOSE_UNITS_BY_INDEX: readonly DoseUnit[] = ['mg', 'mcg', 'ml'];

function coerceEnum<T extends string>(
  raw: unknown,
  allowed: readonly T[],
  byIndex: readonly T[],
): T | undefined {
  if (typeof raw === 'string') {
    const lower = raw.toLowerCase();
    return (allowed as readonly string[]).includes(lower) ? (lower as T) : undefined;
  }
  if (typeof raw === 'number' && Number.isInteger(raw) && raw >= 0 && raw < byIndex.length) {
    return byIndex[raw];
  }
  return undefined;
}

function loadSettings(): Settings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const themeRaw = parsed.theme ?? parsed.Theme;
    const darkRaw = parsed.dark ?? parsed.Dark;
    const weightRaw = parsed.weightUnit ?? parsed.WeightUnit;
    const doseRaw = parsed.doseUnit ?? parsed.DoseUnit;
    return {
      theme:
        coerceEnum<Theme>(themeRaw, ['iron', 'bloom', 'pulse', 'forest', 'cobalt'], THEMES_BY_INDEX) ??
        DEFAULT.theme,
      dark: typeof darkRaw === 'boolean' ? darkRaw : DEFAULT.dark,
      weightUnit:
        coerceEnum<WeightUnit>(weightRaw, ['lb', 'kg'], WEIGHT_UNITS_BY_INDEX) ?? DEFAULT.weightUnit,
      doseUnit:
        coerceEnum<DoseUnit>(doseRaw, ['mg', 'mcg', 'ml'], DOSE_UNITS_BY_INDEX) ?? DEFAULT.doseUnit,
    };
  } catch {
    return DEFAULT;
  }
}

// Per-theme PWA chrome / iOS status-bar colors. Match --bg in themes.css so the
// installed PWA's frame matches the page surface in either mode.
const THEME_COLORS: Record<Theme, { light: string; dark: string }> = {
  iron:   { light: '#F4F2EE', dark: '#0F0E0C' },
  bloom:  { light: '#FBF6F2', dark: '#15100F' },
  pulse:  { light: '#F2F4F5', dark: '#0C0F11' },
  forest: { light: '#F1F5F0', dark: '#0C100D' },
  cobalt: { light: '#F2F3F8', dark: '#0C0E14' },
};

function applyToDocument(s: Settings) {
  const html = document.documentElement;
  const mode = s.dark ? 'dark' : 'light';
  html.setAttribute('data-theme', s.theme);
  html.setAttribute('data-mode', mode);
  // Tell the browser which UA color scheme to use for native form controls,
  // scrollbars, and the iOS status-bar tint. Without this, iOS Safari renders
  // <input>/<select> chrome in the *system* mode (dark on a dark phone) even
  // when our background is light — that's the "dark and invisible" bug.
  html.style.colorScheme = mode;

  // Replace any media-scoped theme-color metas with a single unconditional one
  // so the active mode's color always wins, regardless of system preference.
  const tc = THEME_COLORS[s.theme][mode];
  document.querySelectorAll('meta[name="theme-color"]').forEach((m) => m.remove());
  const meta = document.createElement('meta');
  meta.name = 'theme-color';
  meta.content = tc;
  document.head.appendChild(meta);
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(loadSettings);

  useEffect(() => {
    applyToDocument(settings);
    // Merge rather than overwrite: the locale lives in the same blob (written by
    // LocaleProvider) and must survive theme/unit changes.
    patchSettingsBlob(settings);
  }, [settings]);

  const value: ThemeContextValue = {
    ...settings,
    setTheme: (theme) => setSettings((s) => ({ ...s, theme })),
    setDark: (dark) => setSettings((s) => ({ ...s, dark })),
    setWeightUnit: (weightUnit) => setSettings((s) => ({ ...s, weightUnit })),
    setDoseUnit: (doseUnit) => setSettings((s) => ({ ...s, doseUnit })),
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
